import axiosApiInstance from '@/api/axios';
import type {
  AppDistributionRelease,
  GetReleaseRequest,
  ListReleasesRequest,
  ListReleasesResponse,
} from '@/types/FirebaseAppDistribution';
import {isolateError} from '@/utils/isolateError';

const APP_DISTRIBUTION_BASE_URL = '/v2/admin/app-distribution';

export class AppReleasesService {
  private static instance: AppReleasesService;

  private constructor() {}

  public static getInstance(): AppReleasesService {
    if (!AppReleasesService.instance) {
      AppReleasesService.instance = new AppReleasesService();
    }
    return AppReleasesService.instance;
  }

  /**
   * Get a list of app releases for a specific platform
   */
  async getAppReleases(
    params: ListReleasesRequest,
  ): Promise<ListReleasesResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('platform', params.platform);

    if (params.nextPageToken) {
      queryParams.append('nextPageToken', params.nextPageToken);
    }

    const [response, error] = await isolateError(
      axiosApiInstance.get<ListReleasesResponse>(
        `${APP_DISTRIBUTION_BASE_URL}/releases?${queryParams.toString()}`,
      ),
    );

    if (error) {
      console.error('Error fetching app releases:', error);
      throw new Error('Failed to fetch app releases', {cause: error});
    }
    if (!response) {
      throw new Error('Failed to fetch app releases: empty response');
    }

    return response.data;
  }

  /**
   * Get details for a specific release
   */
  async getReleaseDetails(
    params: GetReleaseRequest,
  ): Promise<AppDistributionRelease> {
    const queryParams = new URLSearchParams();
    queryParams.append('platform', params.platform);

    const [response, error] = await isolateError(
      axiosApiInstance.get<AppDistributionRelease>(
        `${APP_DISTRIBUTION_BASE_URL}/releases/${params.releaseId}?${queryParams.toString()}`,
      ),
    );

    if (error) {
      console.error('Error fetching release details:', error);
      throw new Error('Failed to fetch release details', {cause: error});
    }
    if (!response) {
      throw new Error('Failed to fetch release details: empty response');
    }

    return response.data;
  }
}
